import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/onboard.css';
import { auth } from '../context/auth.ts';
import SentientOrb from '../orb/SentientOrb.tsx'; // Use the base orb!
import { SignInChat } from '../orb/SentientOrbChat.tsx';
import { isValidEmail, getEmailValidationError } from '../utils/validation';

function SignIn({ onClose, onSwitchToSignUp, onSwitchToForgotPassword }) {
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [chatStage, setChatStage] = useState('welcome');
    const [isStreaming, setIsStreaming] = useState(false);
    const [orbAction, setOrbAction] = useState('idle'); // Track current action
    const orbRef = useRef(null);

    
    const handleCharacterTyped = useCallback(() => {
        if (orbRef.current) {
            orbRef.current.triggerPulse();
        }
    }, []);

    const handleStreamStart = useCallback(() => {
        setIsStreaming(true);
        setOrbAction('talk');
    }, []);

    const handleStreamComplete = useCallback(() => {
        setIsStreaming(false);
        setOrbAction('idle');
    }, [chatStage]);

    // THEN EFFECTS
    useEffect(() => {
        setChatStage('welcome');
        setIsStreaming(false);
    }, []);

    useEffect(() => {
        if (isStreaming) {
            setOrbAction('talk');
        } else {
            setOrbAction('idle');
        }
    }, [isStreaming]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const { username, password } = formData;

        if (!username || !password) {
            setError('Please enter both username/email and password');
            setLoading(false);
            return;
        }

        // Email validation
        const emailValidationError = getEmailValidationError(username);
        if (emailValidationError) {
            setError(emailValidationError);
            setLoading(false);
            return;
        }

        try {
            const result = await auth.signIn(username, password);

            if (result?.error) {
                
                const newFailedAttempts = failedAttempts + 1;
                setFailedAttempts(newFailedAttempts);
                
                if (newFailedAttempts >= 3) {
                    setChatStage('thirdAttempt');
                } else {
                    setChatStage('invalidCredentials');
                }
                setIsStreaming(true);
                
                const errorMessage = result.error.message?.toLowerCase() || '';
                
                if (errorMessage.includes('invalid') || errorMessage.includes('credentials')) {
                    setError('Invalid email or password. Please try again.');
                } else if (errorMessage.includes('email not confirmed') || errorMessage.includes('confirm')) {
                    setError('Please check your email and confirm your account before signing in.');
                } else if (errorMessage.includes('too many')) {
                    setError('Too many login attempts. Please wait a few minutes and try again.');
                } else {
                    setError(result.error.message || 'Sign in failed. Please try again.');
                }
                setLoading(false);
                return;
            }

            if (result?.data?.user && result?.data?.session) {
                // Don't navigate here - let the AuthContext listener handle everything
                // This prevents race conditions and ensures smooth navigation
                setLoading(false);
                setChatStage('welcome'); // Reset chat for next time
                // The App.js useEffect will handle navigation based on auth state
            } else {
                setError('Sign in failed. Please try again.');
                setLoading(false);
            }
        } catch (err) {
            
            const newFailedAttempts = failedAttempts + 1;
            setFailedAttempts(newFailedAttempts);
            
            if (newFailedAttempts >= 3) {
                setChatStage('thirdAttempt');
            } else {
                setChatStage('invalidCredentials');
            }
            setIsStreaming(true);
            
            if (err.message?.includes('fetch')) {
                setError('Network error. Please check your connection and try again.');
            } else if (err.message?.includes('timeout')) {
                setError('Request timed out. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setLoading(true);

        try {
            const { error } = await auth.signInWithGoogle();

            if (error) {
                setError('Google sign in failed. Please try again.');
                setLoading(false);
            }
            // Note: For OAuth, the page will redirect, so we don't need to set loading to false
            // The auth state listener will handle the sign-in when user returns
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleSignUpClick = () => {
        if (onSwitchToSignUp) {
            onSwitchToSignUp();
        }
    };

    return (
        <div className="onboard-container signin-container">
            <div className="signin-layout-vertical">
                {onClose && (
                    <button className="back-button-bottom" onClick={onClose} disabled={loading}>
                        <span className="back-button-text">← Back</span>
                        <span className="back-button-icon">←</span>
                    </button>
                )}

                {/* Top Orb Section - Now using base SentientOrb with action prop */}
                <div className="orb-container-top">
                    <SentientOrb 
                        ref={orbRef}
                        color="#001a33" 
                        scale={1.5}
                        action={orbAction}
                        actionIntensity={1}
                    />
                </div>

                {/* Chat Message */}
                <SignInChat 
                    stage={chatStage}
                    onStreamStart={handleStreamStart}
                    onStreamComplete={handleStreamComplete}
                    isStreaming={isStreaming}
                    // onCharacterTyped={handleCharacterTyped}
                    attemptCount={failedAttempts}
                />

                {/* Form Section */}
                <div className="signin-content">
                    <form className="signin-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <label htmlFor="username">Email</label>
                            </div>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                placeholder=""
                                disabled={loading}
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <label htmlFor="password">Password</label>
                                {onSwitchToForgotPassword && (
                                    <span
                                        className="signup-link"
                                        onClick={onSwitchToForgotPassword}
                                        style={{ fontSize: '13px', cursor: 'pointer' }}
                                    >
                                        Forgot password?
                                    </span>
                                )}
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder=""
                                disabled={loading}
                                required
                            />
                        </div>

                        <button type="submit" className="signin-btn" disabled={loading}>
                            {loading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </form>

                    {/* <div className="divider">
                        <span>or</span>
                    </div>

                    <button className="google-signin-btn" onClick={handleGoogleSignIn} disabled={loading}>
                        <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {loading ? 'Redirecting...' : 'Continue with Google'}
                    </button> */}

                    <div className="signin-footer">
                        <p>Don't have an account? <span className="signup-link" onClick={handleSignUpClick}>Sign up here</span></p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SignIn;